import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronDown, Menu, X } from 'lucide-react'
import { Link, NavLink, useLocation } from 'react-router-dom'

import { useAuth } from '@/contexts/AuthContext'
import { useOnlineBookingInbox } from '@/hooks/useOnlineBookingInbox'
import { useUiLocale } from '@/contexts/UiLocaleContext'
import {
  appNavLabel,
  getTopNavMenus,
  isAppNavActive,
} from '@/lib/appNavigation'
import { getNavIcon } from '@/lib/navIcons'
import { cn } from '@/lib/utils'

/**
 * @param {{ open: boolean, onClose: () => void }} props
 */
export function MobileNavSheet({ open, onClose }) {
  const { user } = useAuth()
  const { t } = useUiLocale()
  const location = useLocation()
  const { unreadCount } = useOnlineBookingInbox()
  const menus = useMemo(() => getTopNavMenus(user), [user])

  useEffect(() => {
    if (!open) return undefined
    const onKey = (event) => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-[1px] lg:hidden"
        aria-label="Close menu backdrop"
        onClick={onClose}
      />
      <aside className="fixed inset-y-0 left-0 z-[70] flex w-[min(20rem,88vw)] flex-col bg-[#8A6D42] text-white shadow-xl lg:hidden">
        <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
          <p className="text-sm font-semibold">Menu</p>
          <button
            type="button"
            className="flex h-8 w-8 items-center justify-center rounded-xl text-white/80 hover:bg-white/10"
            aria-label="Close menu"
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 py-3" aria-label="Main">
          {menus.map((group) => (
            <div key={group.id} className="mb-3">
              <p className="px-2 pb-1 text-[10px] font-bold uppercase tracking-widest text-white/50">
                {t(group.labelKey)}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((entry) => {
                  const Icon = getNavIcon(entry.icon)
                  const active = isAppNavActive(entry, location.pathname)
                  const showBadge = entry.id === 'online-bookings' && unreadCount > 0
                  return (
                    <li key={entry.id}>
                      <Link
                        to={entry.path}
                        onClick={onClose}
                        className={cn(
                          'flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors',
                          active ? 'bg-white/15 text-white' : 'text-white/80 hover:bg-white/10 hover:text-white',
                        )}
                      >
                        <Icon className="h-4 w-4 shrink-0" strokeWidth={2.25} />
                        <span className="min-w-0 flex-1 truncate">{appNavLabel(entry, t)}</span>
                        {showBadge ? (
                          <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">
                            {unreadCount > 99 ? '99+' : unreadCount}
                          </span>
                        ) : null}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>
    </>
  )
}

/**
 * @param {{
 *   group: { id: string, labelKey: string, items: import('@/lib/appNavigation').AppNavEntry[] },
 *   pathname: string,
 *   t: (key: string) => string,
 *   unreadCount: number,
 *   open: boolean,
 *   onOpenChange: (open: boolean) => void,
 * }} props
 */
function DesktopMenuGroup({ group, pathname, t, unreadCount, open, onOpenChange }) {
  const triggerRef = useRef(/** @type {HTMLButtonElement | null} */ (null))
  const menuRef = useRef(/** @type {HTMLDivElement | null} */ (null))
  const [coords, setCoords] = useState({ top: 0, left: 0 })
  const groupActive = group.items.some((entry) => isAppNavActive(entry, pathname))
  const single = group.items.length === 1 ? group.items[0] : null

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return undefined
    const update = () => {
      const rect = triggerRef.current.getBoundingClientRect()
      setCoords({ top: rect.bottom + 6, left: rect.left })
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    function onDoc(event) {
      const target = /** @type {Node} */ (event.target)
      if (triggerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      onOpenChange(false)
    }
    function onKey(event) {
      if (event.key === 'Escape') onOpenChange(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onOpenChange])

  if (single) {
    const Icon = getNavIcon(single.icon)
    const active = isAppNavActive(single, pathname)
    const showBadge = single.id === 'online-bookings' && unreadCount > 0
    return (
      <NavLink
        to={single.path}
        end={single.path === '/'}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-[13px] font-semibold tracking-wide transition-all',
          active
            ? 'border-white/35 bg-white/20 text-white shadow-sm shadow-black/10'
            : 'border-white/15 bg-white/5 text-white/90 hover:border-white/30 hover:bg-white/15 hover:text-white',
        )}
      >
        <Icon className="h-3.5 w-3.5 shrink-0 opacity-90" strokeWidth={2.25} />
        <span>{appNavLabel(single, t)}</span>
        {showBadge ? (
          <span className="rounded-full bg-amber-500 px-1 text-[9px] font-bold leading-4 text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        ) : null}
      </NavLink>
    )
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={cn(
          'inline-flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-[13px] font-semibold tracking-wide transition-all',
          groupActive || open
            ? 'border-white/35 bg-white/20 text-white shadow-sm shadow-black/10'
            : 'border-white/15 bg-white/5 text-white/90 hover:border-white/30 hover:bg-white/15 hover:text-white',
        )}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => onOpenChange(!open)}
      >
        <span>{t(group.labelKey)}</span>
        <ChevronDown className={cn('h-3.5 w-3.5 opacity-80 transition-transform', open && 'rotate-180')} />
      </button>
      {open
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={{ top: coords.top, left: coords.left }}
              className="fixed z-[200] min-w-[13rem] rounded-xl border border-[#E5DED3] bg-white p-1.5 shadow-xl shadow-black/10"
            >
              {group.items.map((entry) => {
                const Icon = getNavIcon(entry.icon)
                const active = isAppNavActive(entry, pathname)
                const showBadge = entry.id === 'online-bookings' && unreadCount > 0
                return (
                  <Link
                    key={entry.id}
                    to={entry.path}
                    role="menuitem"
                    onClick={() => onOpenChange(false)}
                    className={cn(
                      'flex items-center gap-2 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors',
                      active
                        ? 'bg-[#F7F4EF] text-[#8A6D42]'
                        : 'text-slate-700 hover:bg-[#F7F4EF] hover:text-[#8A6D42]',
                    )}
                  >
                    <Icon className="h-4 w-4 shrink-0" strokeWidth={2.25} />
                    <span className="min-w-0 flex-1 truncate">{appNavLabel(entry, t)}</span>
                    {showBadge ? (
                      <span className="rounded-full bg-amber-500 px-1.5 text-[10px] font-bold text-white">
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    ) : null}
                  </Link>
                )
              })}
            </div>,
            document.body,
          )
        : null}
    </>
  )
}

/** Desktop horizontal menus — sits after brand on the top bar. */
export function TopMenuBar() {
  const { user } = useAuth()
  const { t } = useUiLocale()
  const location = useLocation()
  const { unreadCount } = useOnlineBookingInbox()
  const menus = useMemo(() => getTopNavMenus(user), [user])
  const [openGroupId, setOpenGroupId] = useState(/** @type {string | null} */ (null))

  useEffect(() => {
    setOpenGroupId(null)
  }, [location.pathname])

  return (
    <nav
      className="hidden min-w-0 flex-1 items-center gap-1.5 overflow-x-auto overflow-y-visible rounded-2xl border border-white/20 bg-white/10 p-1.5 shadow-inner shadow-black/5 lg:flex"
      aria-label="Main"
    >
      {menus.map((group) => (
        <DesktopMenuGroup
          key={group.id}
          group={group}
          pathname={location.pathname}
          t={t}
          unreadCount={unreadCount}
          open={openGroupId === group.id}
          onOpenChange={(next) => setOpenGroupId(next ? group.id : null)}
        />
      ))}
    </nav>
  )
}

/**
 * @param {{ onOpen: () => void }} props
 */
export function MobileMenuButton({ onOpen }) {
  return (
    <button
      type="button"
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white/85 transition-colors hover:bg-white/10 hover:text-white lg:hidden"
      aria-label="Open menu"
      onClick={onOpen}
    >
      <Menu className="h-5 w-5" strokeWidth={2} />
    </button>
  )
}
